import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Grid, Typography, Collapse } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import BackdropDialog from 'common/components/BackdropDialog';
import RolesSelect from './RolesSelect';
import LocationsSelect from './LocationsSelect';
import PermissionsSelect, { PermissionUpdate } from './PermissionsSelect';
import MultiEmailsInput from './MultiEmailsInput';

import { Permission, ShopRole } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';
import { isValidEmail } from 'common/utils/validation';
import errorHandler from 'common/services/errorHandler';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import { getDefaultPermissionsForRoles } from 'common/modules/users/utils';
import { isAdmin } from 'common/modules/users/utils';
import { setOnboardingActionDone } from 'services/onboarding/utils';
import { Callable } from 'common/frontend/callable';
import { getPermissionOptions } from '../utils';

interface FormErrors {
	roles?: string;
	emails?: string;
}

interface Props {
	open: boolean;
	onClose: () => void;
	onSuccess: (invitedEmails: string[]) => void;
}

const InviteUserDialog = (props: Props) => {
	const { open, onClose, onSuccess } = props;
	const classes = useStyles();
	const { t } = useTranslation();

	const activeShopId = useSelector(ShopSelectors.activeShopId)!;
	const shopAnalytics = useSelector(ShopSelectors.activeShopAnalytics);
	const usersData = useSelector(ShopSelectors.usersData);
	const userAccess = useSelector(UserSelectors.userActiveShopAccess);
	const userLocations = useSelector(UserSelectors.userLocations);
	const userHasAccessToAllLocations = useSelector(UserSelectors.userHasAccessToAllLocations);

	const [loading, setLoading] = useState<boolean>(false);

	const [roles, setRoles] = useState<ShopRole[]>([]);
	const [locations, setLocations] = useState<string[] | undefined>(userAccess?.locations);
	const [permissions, setPermissions] = useState<Permission[]>([]);
	const [restrictions, setRestrictions] = useState<Permission[]>([]);
	const [emails, setEmails] = useState<string[]>([]);
	const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
	const [error, setError] = useState('');
	const [formErrors, setFormErrors] = useState<FormErrors>({});

	const defaultPermissions = getDefaultPermissionsForRoles(roles);

	const disabledInvite = !roles.length || !emails.length;

	const handleRolesChange = (roles: ShopRole[]) => {
		setFormErrors((prev) => (prev.roles ? { ...prev, roles: '' } : prev));
		setRoles(roles);
		if (isAdmin(roles)) {
			setPermissions([]);
			setRestrictions([]);
			setLocations(undefined);
		}
	};

	const handlePermissionChange = ({ permissions, restrictions }: PermissionUpdate) => {
		setPermissions(permissions);
		setRestrictions(restrictions);
	};

	const handleEmailsChange = (value: string[]) => {
		setFormErrors((prev) => (prev.emails ? { ...prev, emails: '' } : prev));
		setEmails(value);
	};

	const handleConfirm = async () => {
		if (!roles.length || !emails.length) {
			const formErrors = {
				...(!roles.length && {
					roles: t('common:validation.cannotBeEmpty', { field: t('users.role') }),
				}),
				...(!emails.length && {
					emails: t('common:validation.cannotBeEmpty', { field: t('common:form.emailAddress') }),
				}),
			};
			setFormErrors(formErrors);
			return;
		}
		if (emails.some((email) => !isValidEmail(email))) {
			setFormErrors((pre) => ({
				...pre,
				emails: t('users.emailsMustBeValid', 'All email addresses must be valid.'),
			}));
			return;
		}

		const existingEmails = usersData.map((user) => user.email);
		const usedEmails = emails.filter((email) => existingEmails.includes(email));
		if (usedEmails.length) {
			setInvalidEmails((prev) => [...new Set([...prev, ...usedEmails])]);
			setFormErrors((pre) => ({
				...pre,
				emails: t('shop.emailAlreadyExists', {
					newEmail: usedEmails.join(', '),
					defaultValue: 'Email address {{newEmail}} is already in use by another user.',
				}),
			}));
			return;
		}

		setLoading(true);
		try {
			await Callable.users.inviteUsersToShop({
				shopId: activeShopId,
				emails,
				accessClaims: { roles, permissions, restrictions, locations },
			});
			onClose();
			onSuccess(emails);
			setOnboardingActionDone('USERS', shopAnalytics);
			setLoading(false);
		} catch (e: any) {
			setLoading(false);
			if (e?.details?.validatedEmails) {
				const _invalidEmails = e.details.validatedEmails
					.filter(({ valid }: { valid: boolean }) => {
						return !valid;
					})
					.map(({ email }: { email: string }) => email);
				setInvalidEmails((prev) => [...new Set([...prev, ..._invalidEmails])]);
				setFormErrors((prev) => ({
					...prev,
					emails: t(
						'shop.emailsInvalidOrInUse',
						'Some of the provided email addresses are invalid or already taken by another user',
					),
				}));
				return;
			}
			errorHandler.report(e);
			setError(t('common:errors.somethingWentWrong'));
		}
	};

	const handleClose = (action: 'click_away' | 'cancel' | 'confirm' | 'left-action') => {
		if (action === 'cancel') {
			onClose();
		}
	};

	return (
		<BackdropDialog
			isOpen={open}
			onClose={handleClose}
			label={t('users.inviteUsers', 'Invite users')}
			body={
				<Box>
					<Typography variant="body2">
						{t(
							'users.inviteUsersDescription',
							'Users will receive an email that gives them access to your store.',
						)}
					</Typography>
					<Box mt={3} />
					<Grid container spacing={3} alignItems="center">
						<Grid item xs={12} sm={4}>
							<Typography variant="body1" className={classes.label}>
								{t('users.role')}
							</Typography>
						</Grid>
						<Grid item xs={12} sm={8}>
							<RolesSelect
								value={roles ?? []}
								onChange={handleRolesChange}
								error={formErrors.roles}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography variant="body1" className={classes.label}>
								{t('common:shopLocation.stores')}
							</Typography>
						</Grid>
						<Grid item xs={12} sm={8}>
							<LocationsSelect
								value={locations}
								onChange={setLocations}
								options={userLocations}
								disabled={!!roles.length && isAdmin(roles)}
								allowAllLocations={userHasAccessToAllLocations}
							/>
						</Grid>
					</Grid>
					<Collapse in={!!roles.length && !isAdmin(roles)}>
						<Box className={classes.divider} />
						<Box mt={3}>
							<Typography variant="body1" className={classes.label}>
								{t('users.editPermission', 'Edit permissions')}
							</Typography>
							<Box mt={3} />
							<PermissionsSelect
								permissions={permissions}
								restrictions={restrictions}
								onChange={handlePermissionChange}
								disabled={!roles.length}
								permissionOptions={getPermissionOptions()}
								defaultPermissions={defaultPermissions}
							/>
						</Box>
					</Collapse>

					<Box className={classes.divider} />
					<Typography variant="body1" className={classes.label}>
						{t('common:form.emailAddress')}
					</Typography>
					<Box mt={3} />
					<MultiEmailsInput
						existedEmails={invalidEmails}
						value={emails}
						onChange={handleEmailsChange}
						error={formErrors.emails}
					/>
					{error && (
						<Box mt={1}>
							<Typography variant="body2" color="error">
								{error}
							</Typography>
						</Box>
					)}
				</Box>
			}
			variant="primary"
			zIndex={1300}
			confirm={{
				label: t('common:actions.invite', 'Invite'),
				callback: handleConfirm,
				disabled: disabledInvite,
				loading,
			}}
			cancel={{
				label: t('common:actions.cancel'),
			}}
		/>
	);
};

const useStyles = makeStyles((theme) =>
	createStyles({
		label: {
			fontWeight: 500,
		},
		divider: {
			marginTop: theme.spacing(4),
			marginBottom: theme.spacing(4),
			borderBottom: `1px solid ${theme.palette.divider}`,
		},
	}),
);

export default InviteUserDialog;
